import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonIcon from "@mui/icons-material/Person";
import VoiceChatIcon from "@mui/icons-material/VoiceChat";
import SettingsIcon from "@mui/icons-material/Settings";
import BuildIcon from "@mui/icons-material/Build";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentIcon from "@mui/icons-material/Assessment";

const ViewCallLogDetailsModal = ({ open, onClose, data }) => {
  if (!data) return null;

  const { call_log, assistant_snapshot } = data;

  const getStatusColor = (status) => {
    const colors = {
      Completed: "success",
      Transferred: "info",
      Failed: "error",
      active: "success",
      inactive: "default",
    };
    return colors[status] || "default";
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" fontWeight={600}>
            Call Log Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Call Information Section */}
          <Card sx={{ backgroundColor: "#F9FAFB", border: "1px solid #E0E0E0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <PhoneIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Call Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <InfoRow label="Call ID" value={call_log?.call_id} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow
                    label="Status"
                    value={
                      <Chip
                        label={call_log?.status}
                        size="small"
                        color={getStatusColor(call_log?.status)}
                        sx={{ fontWeight: 500 }}
                      />
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Duration" value={call_log?.duration} icon={<AccessTimeIcon fontSize="small" />} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Cost" value={`$${call_log?.cost?.toFixed(2)}`} icon={<AttachMoneyIcon fontSize="small" />} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Phone Number" value={call_log?.phone_number} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Assistant" value={call_log?.assistant} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Created At" value={new Date(call_log?.created_at).toLocaleString()} />
                </Grid>
                <Grid item xs={6}>
                  <InfoRow label="Updated At" value={new Date(call_log?.updated_at).toLocaleString()} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Assistant Snapshot Section */}
          {assistant_snapshot && (
            <>
              <Divider />
              <Card sx={{ backgroundColor: "#F9FAFB", border: "1px solid #E0E0E0" }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <PersonIcon color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      Assistant Configuration
                    </Typography>
                    <Chip
                      label={assistant_snapshot?.status}
                      size="small"
                      color={getStatusColor(assistant_snapshot?.status)}
                      sx={{ ml: 1 }}
                    />
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <InfoRow label="Name" value={assistant_snapshot?.name} />
                    </Grid>
                    <Grid item xs={6}>
                      <InfoRow label="First Message" value={assistant_snapshot?.first_message || "N/A"} />
                    </Grid>
                    <Grid item xs={6}>
                      <InfoRow label="Model" value={assistant_snapshot?.model || "N/A"} />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Voice Configuration */}
              {assistant_snapshot?.voice && (
                <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <VoiceChatIcon color="info" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Voice Configuration
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <InfoRow label="Provider" value={assistant_snapshot.voice.provider} />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow label="Voice ID" value={assistant_snapshot.voice.voiceId} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* LLM Configuration */}
              {assistant_snapshot?.llm_config && (
                <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <SettingsIcon color="warning" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        LLM Configuration
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <InfoRow label="Provider" value={assistant_snapshot.llm_config.provider} />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow label="Model" value={assistant_snapshot.llm_config.model} />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow label="Temperature" value={assistant_snapshot.llm_config.temperature} />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow label="Max Tokens" value={assistant_snapshot.llm_config.max_tokens} />
                      </Grid>
                      {assistant_snapshot.llm_config.system_prompt && (
                        <Grid item xs={12}>
                          <Box>
                            <Typography variant="caption" color="text.secondary" gutterBottom>
                              System Prompt:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                backgroundColor: "#F5F5F5",
                                p: 1.5,
                                borderRadius: 1,
                                fontFamily: "monospace",
                                fontSize: 12,
                                maxHeight: 150,
                                overflow: "auto"
                              }}
                            >
                              {assistant_snapshot.llm_config.system_prompt}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Transcriber Configuration */}
              {assistant_snapshot?.transcriber && (
                <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <DescriptionIcon color="success" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Transcriber Configuration
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <InfoRow label="Provider" value={assistant_snapshot.transcriber.provider} />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow label="Model" value={assistant_snapshot.transcriber.model} />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow label="Language" value={assistant_snapshot.transcriber.language} />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Tools */}
              {assistant_snapshot?.tools && assistant_snapshot.tools.length > 0 && (
                <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <BuildIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Tools ({assistant_snapshot.tools.length})
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {assistant_snapshot.tools.map((tool, index) => (
                        <Chip
                          key={index}
                          label={tool.name || tool}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Sources */}
              {assistant_snapshot?.sources && assistant_snapshot.sources.length > 0 && (
                <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <DescriptionIcon color="info" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Knowledge Sources ({assistant_snapshot.sources.length})
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {assistant_snapshot.sources.map((source, index) => (
                        <Chip
                          key={index}
                          label={source.name || source}
                          size="small"
                          color="info"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Analysis */}
              {assistant_snapshot?.analysis && (
                <Card sx={{ backgroundColor: "#FFF", border: "1px solid #E0E0E0" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <AssessmentIcon color="secondary" />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Analysis Configuration
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <InfoRow
                          label="Summary"
                          value={assistant_snapshot.analysis.summary ? "Enabled" : "Disabled"}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow
                          label="Structured Data"
                          value={assistant_snapshot.analysis.structuredData ? "Enabled" : "Disabled"}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow
                          label="Success Evaluation"
                          value={assistant_snapshot.analysis.successEvaluation ? "Enabled" : "Disabled"}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper component for info rows
const InfoRow = ({ label, value, icon }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" gutterBottom>
      {label}:
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {icon}
      <Typography variant="body2" fontWeight={500}>
        {value || "N/A"}
      </Typography>
    </Box>
  </Box>
);

export default ViewCallLogDetailsModal;
